/**
 * Signup orchestration
 * Creates the auth user and organization together, and recovers from the
 * half-finished state where the auth account exists but the org doc doesn't
 * (e.g. a previous signup failed between the two writes).
 */

import { User } from 'firebase/auth';
import { signIn, signUp } from './auth.service';
import {
  checkOrganizationExists,
  createOrganization,
} from './organization.service';
import { getAuthErrorCode } from '@/utils/authErrors';

export type RegistrationResult = {
  user: User;
  /** True when this email already had a fully set-up account; we signed them in instead. */
  existingAccount: boolean;
};

/** An error with a message safe to show directly to the user. */
export class SignupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SignupError';
  }
}

export async function registerOrganization(
  email: string,
  password: string,
  orgName: string,
  adminPin: string
): Promise<RegistrationResult> {
  let user: User;

  try {
    user = await signUp(email, password);
  } catch (err) {
    if (getAuthErrorCode(err) !== 'auth/email-already-in-use') {
      throw err;
    }

    // The email already has an auth account. It's either a returning customer
    // or an orphan from a signup that failed after creating the auth user.
    // Their password tells us which — try signing in with it.
    let signedIn: User;
    try {
      signedIn = await signIn(email, password);
    } catch {
      throw new SignupError(
        'An account with this email already exists. Sign in instead, or use “Forgot password?” if you don’t remember your password.'
      );
    }

    if (await checkOrganizationExists(signedIn.uid)) {
      return { user: signedIn, existingAccount: true };
    }

    // Orphaned account — finish the setup it never got.
    user = signedIn;
  }

  try {
    await createOrganization(user.uid, orgName, email, adminPin);
  } catch (err) {
    console.error('Error creating organization during signup:', err);
    // The auth user exists and is signed in; retrying the form re-enters the
    // recovery path above, so this state is no longer a dead end.
    throw new SignupError(
      'Your account was created but we couldn’t finish setting it up. Please tap “Create Account” again.'
    );
  }

  return { user, existingAccount: false };
}
